import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { api, errMsg, fmtMoney, fmtDate } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { FileText, CreditCard, Receipt, CalendarCheck, FileSignature, Key, Camera, Flag, Check } from "lucide-react";

const STATUS_STYLES = {
  confirmed: "text-emerald-400 border-emerald-400/30 bg-emerald-400/10",
  pending_payment: "text-amber-400 border-amber-400/30 bg-amber-400/10",
  cancelled: "text-red-400 border-red-400/30 bg-red-400/10",
  completed: "text-sky-400 border-sky-400/30 bg-sky-400/10",
};

function RentalTimeline({ b }) {
  const today = new Date().toISOString().slice(0, 10);
  const paid = b.status === "confirmed" || b.status === "completed";
  const pickupDue = paid && (b.status === "completed" || today >= b.start_date);
  const steps = [
    { key: "booked", label: "Booked", icon: CalendarCheck, done: true },
    { key: "paid", label: "Paid", icon: CreditCard, done: paid },
    { key: "contract", label: "Contract", icon: FileSignature, done: !!b.contract_signed_at, to: !b.contract_signed_at ? `/contract/${b.id}` : null, hint: "Tap to sign" },
    { key: "pickup", label: "Pickup", icon: Key, done: pickupDue, to: paid && !pickupDue ? `/checkin/${b.id}` : null, hint: "Express check-in" },
    { key: "photos", label: "Return photos", icon: Camera, done: !!b.return_submitted, to: b.status === "confirmed" && !b.return_submitted ? `/contract/${b.id}` : null, hint: "Tap to upload" },
    { key: "done", label: "Completed", icon: Flag, done: b.status === "completed" },
  ];
  return (
    <div data-testid={`rental-timeline-${b.id}`} className="mt-5 flex items-start">
      {steps.map((s, i) => {
        const actionable = s.to && !s.done;
        const stepBody = (
          <div className={`flex flex-col items-center gap-1.5 ${actionable ? "group cursor-pointer" : ""}`}>
            <span className={`w-7 h-7 rounded-full border flex items-center justify-center transition-all duration-300 ${
              s.done
                ? "border-primary bg-primary text-primary-foreground"
                : actionable
                  ? "border-primary text-primary group-hover:scale-110 group-hover:bg-primary/10"
                  : "border-border text-muted-foreground"
            }`}>
              {s.done ? <Check size={13} /> : <s.icon size={12} />}
            </span>
            <span className={`text-[9px] font-ui tracking-wide uppercase whitespace-nowrap ${
              s.done ? "text-foreground" : actionable ? "text-primary underline underline-offset-2" : "text-muted-foreground"
            }`}>
              {s.label}
            </span>
            {actionable && <span className="text-[8px] font-ui text-primary/70 whitespace-nowrap -mt-1">{s.hint}</span>}
          </div>
        );
        return (
          <div key={s.key} className="flex items-center flex-1 last:flex-none">
            {actionable ? <Link data-testid={`timeline-link-${s.key}-${b.id}`} to={s.to}>{stepBody}</Link> : stepBody}
            {i < steps.length - 1 && <div className={`h-px flex-1 mx-2 mt-3.5 transition-colors duration-300 ${steps[i + 1].done ? "bg-primary" : "bg-border"}`} />}
          </div>
        );
      })}
    </div>
  );
}

function RoadsideCountdown({ b }) {
  const [now, setNow] = useState(Date.now());
  useEffect(() => {
    const t = setInterval(() => setNow(Date.now()), 60000);
    return () => clearInterval(t);
  }, []);
  if (!b.roadside || !b.roadside_activates_at) return null;
  const diff = new Date(b.roadside_activates_at).getTime() - now;
  if (diff <= 0) {
    return <p data-testid={`roadside-active-${b.id}`} className="mt-2 text-xs font-ui text-emerald-600 flex items-center gap-1.5"><Check size={12} /> 24/7 roadside assistance: ACTIVE</p>;
  }
  const h = Math.floor(diff / 3600000);
  const m = Math.floor((diff % 3600000) / 60000);
  return <p data-testid={`roadside-countdown-${b.id}`} className="mt-2 text-xs font-ui text-amber-600">Roadside assistance activates in {h}h {m}m</p>;
}

function DepositCountdown({ b }) {
  const [now, setNow] = useState(Date.now());
  useEffect(() => {
    const t = setInterval(() => setNow(Date.now()), 3600000);
    return () => clearInterval(t);
  }, []);
  if ((b.deposit_status !== "released" && b.deposit_status !== "refunded") || !b.deposit_released_at) return null;
  const target = new Date(b.deposit_released_at).getTime() + 14 * 86400000;
  const daysLeft = Math.ceil((target - now) / 86400000);
  const releasedOn = new Date(b.deposit_released_at).toISOString().slice(0, 10);
  if (daysLeft > 0) {
    return (
      <p data-testid={`deposit-countdown-${b.id}`} className="mt-1.5 text-xs font-ui text-amber-600">
        Deposit released {fmtDate(releasedOn)} — back on your card in ~{daysLeft} day{daysLeft === 1 ? "" : "s"} (bank processing)
      </p>
    );
  }
  return (
    <p data-testid={`deposit-arrived-${b.id}`} className="mt-1.5 text-xs font-ui text-emerald-600 flex items-center gap-1.5">
      <Check size={12} /> Deposit released {fmtDate(releasedOn)} — it should now be back on your card
    </p>
  );
}

export default function Account() {
  const { user, loading } = useAuth();
  const navigate = useNavigate();
  const [bookings, setBookings] = useState(null);
  const [photosFor, setPhotosFor] = useState(null);
  const [photoGroups, setPhotoGroups] = useState(null);

  const togglePhotos = async (b) => {
    if (photosFor === b.id) { setPhotosFor(null); return; }
    setPhotosFor(b.id);
    setPhotoGroups(null);
    try {
      const { data } = await api.get(`/bookings/${b.id}/photos`);
      setPhotoGroups(data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not load photos");
      setPhotosFor(null);
    }
  };
  const [loyalty, setLoyalty] = useState(null);

  useEffect(() => {
    if (!loading && user === false) navigate("/login");
  }, [user, loading, navigate]);

  useEffect(() => {
    if (user) {
      api.get("/bookings/my").then((r) => setBookings(r.data)).catch(() => setBookings([]));
      api.get("/loyalty/status", { params: { email: user.email } }).then((r) => setLoyalty(r.data)).catch(() => {});
    }
  }, [user]);

  const payNow = async (bookingId) => {
    try {
      const { data } = await api.post(`/bookings/${bookingId}/pay/stripe`, {
        origin_url: window.location.origin,
      });
      window.location.href = data.checkout_url;
    } catch (e) {
      toast.error(errMsg(e));
    }
  };

  if (loading || !user) return <div className="pt-40 pb-24 text-center text-muted-foreground font-ui">Loading…</div>;

  return (
    <div data-testid="account-page" className="max-w-5xl mx-auto px-6 lg:px-10 pt-32 pb-24">
      <img src="/logo.png" alt="Easy Drive Car Rental" data-testid="account-logo" className="h-16 w-auto object-contain mb-5" />
      <p className="text-[11px] font-ui tracking-[0.3em] uppercase text-primary">My Account</p>
      <h1 className="mt-3 font-display text-4xl sm:text-5xl tracking-tight">
        Hey, <span className="italic gold-text">{user.name?.split(" ")[0]}</span>
      </h1>
      <p className="mt-2 text-sm text-muted-foreground font-ui">{user.email}</p>

      {loyalty && (
        <div data-testid="loyalty-card" className="mt-8 bg-card border border-primary/30 rounded-2xl p-6 flex flex-wrap items-center justify-between gap-4">
          <div>
            <p className="text-[11px] font-ui tracking-[0.3em] uppercase text-primary">Easy Drive Loyalty</p>
            <p className="mt-2 font-display text-2xl tracking-tight">
              {loyalty.tier} <span className="gold-text">−{loyalty.percent}%</span>
            </p>
            {loyalty.member_no && (
              <p data-testid="loyalty-member-no" className="mt-1 text-[11px] font-ui tracking-[0.2em] uppercase text-muted-foreground">Loyalty No. {loyalty.member_no}</p>
            )}
            <p data-testid="loyalty-rentals-count" className="mt-1 text-xs text-muted-foreground font-ui">
              Rented <span className="text-foreground font-semibold">{loyalty.rentals}</span> {loyalty.rentals === 1 ? "time" : "times"} — your tier discount applies automatically at checkout.
            </p>
            {loyalty.next_tier && (
              <div data-testid="loyalty-progress" className="mt-3 max-w-xs">
                <div className="h-1.5 rounded-full bg-black/10 overflow-hidden">
                  <div className="h-full rounded-full bg-primary transition-[width] duration-500" style={{ width: `${Math.min(100, (loyalty.rentals / (loyalty.rentals + loyalty.rentals_to_next)) * 100)}%` }} />
                </div>
              </div>
            )}
            {loyalty.perks && loyalty.perks.length > 0 && (
              <ul data-testid="loyalty-perks" className="mt-3 space-y-1">
                {loyalty.perks.map((p) => (
                  <li key={p} className="flex items-center gap-2 text-xs font-ui text-foreground/80">
                    <span className="w-1.5 h-1.5 rounded-full bg-primary shrink-0" /> {p}
                  </li>
                ))}
              </ul>
            )}
          </div>
          {loyalty.next_tier && (
            <div className="text-right">
              <p className="text-xs font-ui text-muted-foreground">{loyalty.rentals_to_next} more {loyalty.rentals_to_next === 1 ? "rental" : "rentals"} to</p>
              <p className="font-display text-lg tracking-tight gold-text">{loyalty.next_tier}</p>
            </div>
          )}
          <Link to="/loyalty-card" className="shrink-0">
            <Button data-testid="loyalty-card-button" variant="outline" className="rounded-full border-primary/40 bg-transparent text-primary hover:bg-primary hover:text-primary-foreground font-ui">
              <CreditCard size={14} className="mr-2" /> Loyalty Card
            </Button>
          </Link>
        </div>
      )}

      <div className="mt-12">
        <h2 className="font-ui text-xs tracking-[0.2em] uppercase text-muted-foreground">Rental History & Invoices</h2>
        {bookings === null ? (
          <p className="mt-8 text-muted-foreground font-ui">Loading bookings…</p>
        ) : bookings.length === 0 ? (
          <div data-testid="account-no-bookings" className="mt-8 bg-card border border-border rounded-2xl p-10">
            <p className="text-muted-foreground">No bookings yet.</p>
            <Link to="/fleet">
              <Button data-testid="account-book-first-button" className="mt-6 rounded-full bg-primary text-primary-foreground hover:bg-primary/90 font-ui">
                Browse the Fleet
              </Button>
            </Link>
          </div>
        ) : (
          <div className="mt-6 space-y-5">
            {bookings.map((b) => (
              <div key={b.id} data-testid={`account-booking-${b.id}`} className="bg-card border border-border rounded-2xl p-6 flex flex-col md:flex-row md:items-center gap-6 justify-between">
                <div>
                  <div className="flex items-center gap-3 flex-wrap">
                    <span className="gold-text font-ui font-semibold">{b.booking_ref}</span>
                    <span data-testid={`booking-status-${b.id}`} className={`text-[10px] font-ui tracking-[0.15em] uppercase border rounded-full px-3 py-1 ${STATUS_STYLES[b.status] || ""}`}>
                      {b.status.replace("_", " ")}
                    </span>
                    {b.deposit_status && (
                      <span data-testid={`deposit-status-${b.id}`} className="text-[10px] font-ui tracking-[0.15em] uppercase border rounded-full px-3 py-1 text-muted-foreground border-border">
                        deposit {b.deposit_status}
                      </span>
                    )}
                  </div>
                  <p className="mt-2 font-display text-2xl tracking-tight">{b.vehicle_name}</p>
                  <p className="mt-1 text-sm text-muted-foreground font-ui">
                    {fmtDate(b.start_date)}{b.pickup_time ? ` · ${b.pickup_time}` : ""} → {fmtDate(b.end_date)}{b.dropoff_time ? ` · ${b.dropoff_time}` : ""} · {b.days} {b.days === 1 ? "day" : "days"} · {fmtMoney(b.total)}
                  </p>
                  <p className="mt-1 text-xs text-muted-foreground font-ui">{b.pickup_location}</p>
                  {b.status !== "cancelled" && <RentalTimeline b={b} />}
                  <RoadsideCountdown b={b} />
                  <DepositCountdown b={b} />
                  {b.status === "confirmed" && (
                    <div data-testid={`pickup-qr-${b.id}`} className="mt-4 flex items-center gap-3">
                      <img src={`${process.env.REACT_APP_BACKEND_URL}/api/bookings/${b.id}/qr.png`} alt="Pickup QR code" className="w-20 h-20 rounded-lg border border-border bg-white p-1" />
                      <p className="text-xs text-muted-foreground font-ui max-w-[190px]">Quick pickup — our team scans this QR on arrival to start your rental instantly</p>
                    </div>
                  )}
                  <Button data-testid={`photos-button-${b.id}`} size="sm" variant="outline" onClick={() => togglePhotos(b)} className="mt-4 rounded-full border-border bg-transparent hover:bg-black/5 hover:text-primary font-ui">
                    {photosFor === b.id ? "Hide My Photos" : "My Photos"}
                  </Button>
                  {photosFor === b.id && (
                    <div data-testid={`photos-panel-${b.id}`} className="mt-3 border border-border rounded-xl p-4 space-y-3">
                      {!photoGroups && <p className="text-xs text-muted-foreground font-ui">Loading photos…</p>}
                      {photoGroups && [["documents", "Documents (license / passport)"], ["pickup", "Pickup inspection photos"], ["return", "Return photos"]].map(([key, label]) => (
                        <div key={key}>
                          <p className="font-ui text-[10px] tracking-[0.2em] uppercase text-muted-foreground">{label}</p>
                          {photoGroups[key].length === 0 ? (
                            <p className="mt-1 text-xs text-muted-foreground font-ui">None yet.</p>
                          ) : (
                            <div className="mt-1.5 flex flex-wrap gap-x-4 gap-y-1.5">
                              {photoGroups[key].map((p) => (
                                <a key={p.id} data-testid={`photo-link-${p.id}`} href={`${process.env.REACT_APP_BACKEND_URL}${p.url}`} target="_blank" rel="noopener noreferrer" className="text-xs font-ui text-primary underline underline-offset-2">{p.label}</a>
                              ))}
                            </div>
                          )}
                        </div>
                      ))}
                    </div>
                  )}
                </div>
                <div className="flex gap-3 shrink-0">
                  {b.status === "pending_payment" && (
                    <Button data-testid={`pay-now-button-${b.id}`} onClick={() => payNow(b.id)} className="rounded-full bg-primary text-primary-foreground hover:bg-primary/90 font-ui">
                      <CreditCard size={14} className="mr-2" /> Pay Now
                    </Button>
                  )}
                  {b.status === "confirmed" && (
                    <Link to={`/extend/${b.id}`}>
                      <Button data-testid={`extend-booking-button-${b.id}`} variant="outline" className="rounded-full border-primary/40 bg-transparent text-primary hover:bg-primary hover:text-primary-foreground font-ui">
                        Extend
                      </Button>
                    </Link>
                  )}
                  {b.status === "confirmed" && (
                    <Link to={`/upgrade/${b.id}`}>
                      <Button data-testid={`upgrade-booking-button-${b.id}`} variant="outline" className="rounded-full border-primary/40 bg-transparent text-primary hover:bg-primary hover:text-primary-foreground font-ui">
                        Upgrade
                      </Button>
                    </Link>
                  )}
                  {b.status === "confirmed" && !b.roadside && new Date().toISOString().slice(0, 10) >= b.start_date && b.end_date > new Date().toISOString().slice(0, 10) && (
                    <Button
                      data-testid={`account-roadside-${b.id}`}
                      variant="outline"
                      onClick={async () => {
                        const remaining = Math.max(0, Math.ceil((new Date(b.end_date) - new Date()) / 86400000));
                        if (!window.confirm(`Add 24/7 roadside assistance for the remaining ${remaining} day(s) at $15/day plus 3% processing? Coverage activates after a short waiting period.`)) return;
                        try {
                          const { data } = await api.post(`/bookings/${b.id}/addon/roadside`, { origin_url: window.location.origin });
                          window.location.href = data.checkout_url;
                        } catch (e) {
                          toast.error(e?.response?.data?.detail || "Could not start roadside checkout");
                        }
                      }}
                      className="rounded-full border-primary/40 bg-transparent text-primary hover:bg-primary hover:text-primary-foreground font-ui"
                    >
                      + Roadside $15/day
                    </Button>
                  )}
                  {(b.status === "confirmed" || b.status === "completed") && (
                    <Link to={`/receipt/${b.id}`}>
                      <Button data-testid={`receipt-button-${b.id}`} variant="outline" className="rounded-full border-border bg-transparent hover:bg-black/5 hover:text-primary font-ui">
                        <Receipt size={14} className="mr-2" /> Invoice
                      </Button>
                    </Link>
                  )}
                  <Link to={`/contract/${b.id}`}>
                    <Button data-testid={`view-contract-button-${b.id}`} variant="outline" className="rounded-full border-border bg-transparent hover:bg-black/5 hover:text-primary font-ui">
                      <FileText size={14} className="mr-2" /> Contract
                    </Button>
                  </Link>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
